// src/pages/AssetAssignLogsPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Loader2, Package, CheckCircle, XCircle, Trash2, AlertCircle, MoreVertical, Eye } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import PageHeader from '../../components/common/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import LogDataDisplay from '../../components/common/LogDataDisplay';
import {
  fetchActivityLogs,
  clearError as clearLogError,
} from '../../store/slices/activityLogSlice';
import { formatDateTime } from 'utlis/formateDateTime';

const getActionBadge = (type) => {
  switch (type) {
    case 'created': return { variant: 'default', icon: Package, label: 'Created' };
    case 'status_change': return { variant: 'warning', icon: AlertCircle, label: 'Status Changed' };
    case 'deleted': return { variant: 'destructive', icon: Trash2, label: 'Deleted' };
    case 'accepted': return { variant: 'success', icon: CheckCircle, label: 'Accepted' };
    case 'rejected': return { variant: 'destructive', icon: XCircle, label: 'Rejected' };
    default: return { variant: 'secondary', icon: Package, label: type || 'Unknown' };
  }
};

const AssetAssignLogsPage = () => {
  usePermissionSocket();

  const dispatch = useDispatch();
  const { logs, pagination, isLoading, error } = useSelector((state) => state.activityLog);

  const { canRead, canExport } = usePermissions('asset_assign_log');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Reset to page 1 when searching
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Refetch with resource=asset_assign filter using activity log API
  const refetch = useCallback(() => {
    if (canRead) {
      dispatch(fetchActivityLogs({
        page,
        page_size: pageSize,
        search: searchTerm,
        resource: 'asset_assign'  // Only asset assign logs
      }));
    }
  }, [dispatch, page, pageSize, searchTerm, canRead]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load asset assignment logs');
      dispatch(clearLogError());
    }
  }, [error, dispatch]);

  const getUserName = (log) => {
    if (!log.created_by) return 'System';
    const { first_name = '', last_name = '', code = '' } = log.created_by || {};
    return `${first_name} ${last_name}`.trim() || code || 'Unknown User';
  };

  const getUserCode = (log) => {
    return log.created_by?.code || 'N/A';
  };

  const generateSummary = (log) => {
    const user = getUserName(log);
    const action = log.action_type;
    const asset = log.data?.asset_name || log.object_repr || 'unknown asset';

    switch (action) {
      case 'created': return `${user} assigned asset "${asset}"`;
      case 'status_change': return `${user} changed status of asset "${asset}"`;
      case 'deleted': return `${user} deleted asset assignment "${asset}"`;
      case 'accepted': return `${user} accepted asset "${asset}"`;
      case 'rejected': return `${user} rejected asset "${asset}"`;
      default: return `${user} performed "${action}" on asset "${asset}"`;
    }
  };

  // Transform log data to match the expected format for LogDataDisplay
  const transformLogData = (log) => {
    if (!log.data) return null;

    // If the data already has the expected format, return as is
    if (log.data.old || log.data.new || log.data.changes) {
      return log.data;
    }

    // Otherwise, transform the flat data structure to the expected format
    // For create actions, put everything in 'new'
    if (log.action_type === 'create') {
      return {
        new: log.data,
        old: {},
        changes: {}
      };
    }

    // For delete actions, put everything in 'old'
    if (log.action_type === 'delete') {
      return {
        old: log.data,
        new: {},
        changes: {}
      };
    }

    // For update actions, try to determine what changed
    return {
      old: log.data.old || {},
      new: log.data.new || {},
      changes: log.data.changes || {}
    };
  };

  const getResourceBadge = (resource) => {
    const map = { asset_assign: 'secondary' };
    return <Badge variant={map[resource] || 'default'}>{resource || 'unknown'}</Badge>;
  };

  const handleViewDetails = (log) => {
    if (!canRead) {
      toast.error("You don't have permission to view details");
      return;
    }
    setSelectedLog(log);
    setIsModalOpen(true);
  };

  const columns = [
    {
      accessorKey: 'resource_display',
      header: 'Resource',
      cell: ({ row }) => getResourceBadge(row.original.resource_display),
    },
    // {
    //   header: 'Asset',
    //   cell: ({ row }) => {
    //     const assetName = row.original.data?.asset_name || row.original.object_repr || '—';
    //     return <span className="font-medium">{assetName}</span>;
    //   },
    // },
    {
      header: 'Action',
      cell: ({ row }) => {
        const { variant, icon: Icon, label } = getActionBadge(row.original.action_display);
        return (
          <Badge variant={variant} className="font-medium">
            <Icon className="w-3 h-3 mr-1" />
            {label}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'created_by',
      header: 'Performed By',
      cell: ({ row }) => <span className="font-medium">{getUserName(row.original)}</span>,
    },
    {
      accessorKey: 'created_at',
      header: 'Timestamp',
      cell: ({ row }) => {
        return (
          <div className="text-sm">
            <div>{formatDateTime(row.original.created_at)}</div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'View',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleViewDetails(row.original)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Asset Assignment Logs"
        description="Complete audit trail of asset assignment actions"
      />

      {/* {isLoading && logs.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin mr-3" />
          <span className="text-lg">Loading asset assignment logs...</span>
        </div>
      )} */}

      {!canRead && !isLoading && (
        <div className="text-center py-16 text-muted-foreground text-lg">
          You don't have permission to view asset assignment logs.
        </div>
      )}

      {canRead && (
        <DataTable
          data={logs}
          totalCount={pagination.count}
          columns={columns}
          searchPlaceholder="Search by resource, action, performed by..."
          showExport={false}
          onExport={() => toast.info("Export coming soon")}
          showImport={false}
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={pagination.total_pages || 1}
          onPageChange={(i) => setPage(i + 1)}
          onPageSizeChange={setPageSize}
          onSearch={setSearchTerm}
          isLoading={isLoading}
        />
      )}

      {/* Details Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-xl">Asset Assignment Log Details</DialogTitle>
            <DialogDescription>
              {selectedLog && formatDateTime(selectedLog.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <ScrollArea className="max-h-[65vh] pr-4">
              <div className="space-y-8 py-4">
                {/* Summary Card */}
                <div className="bg-card border rounded-lg p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-muted-foreground">Performed by</p>
                      <p className="text-lg font-semibold">{getUserName(selectedLog)}</p>
                      <p className="text-sm text-muted-foreground">Code: {getUserCode(selectedLog)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Asset</p>
                      {/* <p className="text-lg font-bold">{selectedLog.data?.asset_name || selectedLog.object_repr || '—'}</p> */}
                      <div className="mt-2">
                        {getResourceBadge(selectedLog.resource_display || selectedLog.resource)}
                      </div>
                      <div className="mt-2">
                        {(() => {
                          const { variant, icon: Icon, label } = getActionBadge(selectedLog.action_display);
                          return (
                            <Badge variant={variant} className="font-medium">
                              <Icon className="w-3 h-3 mr-1" />
                              {label}
                            </Badge>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground">Summary</p>
                    <p className="mt-1 text-foreground">{generateSummary(selectedLog)}</p>
                  </div> */}
                </div>

                {/* Log Data Display - Reusable Component */}
                <LogDataDisplay logData={transformLogData(selectedLog)} />
              </div>
            </ScrollArea>
          )}

          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetAssignLogsPage;